import { cn } from "@/lib/utils";
import type { PromptBinding } from "@/utils/trace/model";

/**
 * The prompt version a block of this turn was built from.
 *
 * A button only when something can actually open it. An earlier pass styled
 * these as chips inside the row's own button: they looked pressable, every one
 * of them opened the same node detail because the click simply bubbled, and
 * pressing one while that detail was open toggled it shut. Nesting a button in
 * a button is invalid markup and it behaved exactly as badly as that implies.
 *
 * Without `onOpen` — a mount that cannot fetch, a replay — it renders as plain
 * text. Promising a panel that will never arrive is worse than saying nothing.
 */
export function PromptBindingBadge({
  binding,
  open,
  onOpen,
}: {
  binding: PromptBinding;
  open?: boolean;
  onOpen?: (binding: PromptBinding) => void;
}) {
  if (!onOpen) {
    return (
      <span className="text-xs text-muted-foreground">{binding.label}</span>
    );
  }
  return (
    <button
      type="button"
      data-prompt-binding={binding.name}
      aria-pressed={open ?? false}
      onClick={() => onOpen(binding)}
      className={cn(
        "cursor-pointer rounded-full border px-1.5 text-xs hover:bg-surface-subtle",
        open
          ? "border-foreground bg-surface-subtle text-foreground"
          : "border-border text-muted-foreground",
      )}
    >
      {binding.label}
    </button>
  );
}

/**
 * A fact about the turn with nothing behind it to open.
 *
 * Kept visually apart from the badge above on purpose: a rounded border means
 * "this opens something" here, and these open nothing.
 */
export function TurnFlag({ children }: { children: string }) {
  return <span className="text-xs text-muted-foreground">{children}</span>;
}
